/**
 * Cicha Pewność w palecie marki klienta - wspólne dane prowadzą do rozmowy, konkretnego terminu i lokalnego spotkania bez deklaracji ponad dostępne źródła.
 */
export type FaqItem = { question: string; answer: string };
export type CityType = "office" | "remote";
export type MediaAttribution = {
  author: string;
  sourceUrl: string;
  license: string;
  licenseUrl: string;
  changeNote?: string;
};
export type OfficeMedia = {
  src: string;
  width: number;
  height: number;
  alt: string;
  caption?: string;
  attribution?: MediaAttribution;
};
export type Office = {
  city: string;
  name: string;
  slug: string;
  gen: string;
  loc: string;
  type: CityType;
  address?: string;
  mapsUrl?: string;
  googleListingUrl?: string;
  googleListingQuery?: string;
  googleListingCid?: string;
  officePhoto?: string;
  heroMedia?: OfficeMedia;
  galleryMedia?: OfficeMedia[];
  locationMedia?: OfficeMedia[];
  nearby: string[];
  regionLine: string;
  courtInfo?: string;
  cityIn: string;
  detail: string;
  localAngle: string;
  meetingNote: string;
  localFocus: string;
  serviceMode: "office" | "city";
  coords: { lat: number; lng: number };
  verifiedAddress: boolean;
};
type OfficeBase = Omit<
  Office,
  | "name"
  | "gen"
  | "loc"
  | "type"
  | "mapsUrl"
  | "googleListingUrl"
  | "googleListingQuery"
  | "googleListingCid"
  | "officePhoto"
  | "heroMedia"
  | "galleryMedia"
  | "locationMedia"
  | "regionLine"
  | "courtInfo"
  | "nearby"
> & { address: string; nearby: string };
type CityDetails = Pick<
  Office,
  | "name"
  | "gen"
  | "loc"
  | "type"
  | "address"
  | "googleListingUrl"
  | "googleListingQuery"
  | "googleListingCid"
  | "officePhoto"
  | "heroMedia"
  | "galleryMedia"
  | "locationMedia"
  | "nearby"
  | "courtInfo"
>;

export const contact = {
  phoneDisplay: "513 513 613",
  phoneHref: "tel:+48513513613",
  email: "biuro@upadlosci-ekspert.pl",
  whatsapp:
    "https://wa.me/48513513613?text=Dzie%C5%84%20dobry%2C%20chc%C4%99%20sprawdzi%C4%87%20swoj%C4%85%20sytuacj%C4%99.",
  address: "ul. Przemysłowa 10, 40-020 Katowice",
  hours: "Infolinia: pon.-pt. 8:00 - 20:00",
  callbackTime: "15 minut",
};

export const siteFacts = {
  dataAsOf: "wrzesień 2026",
  ratingText: "5,0 · 249 opinii z wizytówek Google",
  officesAndCitiesText:
    "Spotkania w 6 kancelariach, obsługa zdalna na terenie całego kraju, a zwłaszcza w miastach wojewódzkich.",
  cityCount: 21,
};

export const awardsMedia = {
  src: "/manus-storage/wyroznienia-upadlosci-ekspert_c827a977.jpeg",
  width: 2000,
  height: 1500,
  alt: "Wyróżnienia Upadłości Ekspert: Orły Prawa 2024 i Certyfikat Firma Godna Zaufania 2026",
};

export const awardSources = {
  orlyPrawa: "https://www.orlyprawa.pl/profile-444199-upadlosci-ekspert",
  firmaGodnaZaufania:
    "https://www.firmagodnazaufania.pl/company,1007880,upadlosci-ekspert-pl",
};

export const courtFee = {
  amount: "30 zł",
  label: "Opłata sądowa od wniosku: 30 zł",
  explanation:
    "To opłata urzędowa za złożenie wniosku, niezależna od wynagrodzenia za prowadzenie sprawy.",
};

const sharedCityHeroMedia: OfficeMedia = {
  src: "/manus-storage/kancelaria-wspolna-dla-miast_4bbdf41e.webp",
  width: 1440,
  height: 1080,
  alt: "Wnętrze kancelarii Upadłości Ekspert",
};

export const stats = [
  {
    value: "5 789",
    label: "przeprowadzonych upadłości",
    note: "Liczba upadłości przeprowadzonych przez nasz zespół. Stan: wrzesień 2026.",
  },
  {
    value: "61 mln zł",
    label: "największy umorzony dług jednego Klienta",
    note: "Najwyższy dług umorzony w jednej sprawie naszego klienta. Stan: wrzesień 2026.",
  },
  {
    value: "26 lat",
    label: "doświadczenia naszego zespołu",
    note: "Doświadczenie zespołu prowadzącego sprawy. Stan: wrzesień 2026.",
  },
  {
    value: "6 kancelarii",
    label: "i obsługa całego kraju",
    note: "Spotkania w 6 kancelariach, obsługa zdalna na terenie całego kraju, a zwłaszcza w miastach wojewódzkich.",
  },
];

export const faqItems: FaqItem[] = [
  {
    question: "Czy muszę iść do sądu?",
    answer:
      "Najczęściej nie. Wniosek o ogłoszenie upadłości jest rozpatrywany na posiedzeniu niejawnym. Jeżeli w konkretnej sprawie potrzebne będzie osobiste działanie, wyjaśnimy to przed podjęciem kroku.",
  },
  {
    question: "Czy pomagacie też po ogłoszeniu upadłości?",
    answer:
      "Tak. Pomagamy także na kolejnym etapie: przy kontakcie z syndykiem, ankiecie, portalu Krajowego Rejestru Zadłużonych, pismach i planie spłaty. Wiele firm kończy pomoc na samym wniosku, a my pozostajemy przy sprawie dalej.",
  },
  {
    question: "Czy syndyk mi pomoże?",
    answer:
      "Syndyk jest neutralny między sądem, wierzycielami i dłużnikiem - nie jest Twoim pełnomocnikiem ani doradcą. My pomagamy Ci zrozumieć pisma, terminy i kolejne kroki w Twojej sprawie.",
  },
  {
    question:
      "Nie mam wszystkich dokumentów i nie pamiętam wszystkich długów. Co wtedy?",
    answer:
      "To nie przekreśla pierwszej rozmowy. Wystarczy zarys sytuacji albo jedno pismo, które dziś budzi największy niepokój. Pomożemy skompletować dokumenty i przygotować wnioski, aby uzupełnić historię zobowiązań.",
  },
  {
    question: "Od jakiej kwoty zadłużenia prowadzicie sprawy?",
    answer:
      "Prowadzimy sprawy od 30 000 zł zadłużenia wzwyż. Przy niższej kwocie koszty i czas postępowania nie zawsze są proporcjonalne do korzyści, dlatego warto najpierw omówić inne możliwe rozwiązania.",
  },
  {
    question: "Czy mogę płacić w ratach?",
    answer:
      "Możliwa płatność w ratach. Warunki i sposób rozliczenia omawiamy przed decyzją, po poznaniu sytuacji i zakresu potrzebnej pomocy.",
  },
  {
    question: "Ile kosztuje prowadzenie sprawy?",
    answer: `I etap prowadzenia sprawy kosztuje 2 900 - 4 900 zł. Obejmuje analizę sytuacji, skompletowanie dokumentów, przygotowanie i złożenie wniosku oraz prowadzenie sprawy aż do postanowienia sądu o ogłoszeniu upadłości. ${courtFee.label}. ${courtFee.explanation} Po ogłoszeniu upadłości zakres i koszt dalszego wsparcia ustalamy indywidualnie.`,
  },
  {
    question: "Czy obsługujecie moje miasto?",
    answer:
      "Spotkania organizujemy w 6 kancelariach, a sprawy prowadzimy zdalnie w całej Polsce. Możesz zacząć od telefonu lub rozmowy online, a formę spotkania ustalimy wspólnie.",
  },
  {
    question: "Ile trwa sprawa i od czego zależy plan spłaty?",
    answer:
      "Czas postępowania i ewentualny plan spłaty zależą od sytuacji finansowej, majątku, dochodów, dokumentów oraz decyzji sądu. Celem jest ocena możliwie najkorzystniejszego, jak najmniejszego i jak najkrótszego planu spłaty, bez obiecywania konkretnego wyniku.",
  },
  {
    question: "Czy stracę mieszkanie?",
    answer:
      "To ważny temat do indywidualnej analizy. Znaczenie mają m.in. wartość mieszkania, sytuacja rodzinna oraz obciążenia. Na pierwszej rozmowie wyjaśnimy, jakie scenariusze warto rozważyć w Twojej sprawie.",
  },
  {
    question: "Czy dowie się mój pracodawca?",
    answer:
      "Nie ma obowiązku, abyś sam informował pracodawcę o rozmowie z nami lub o przygotowaniu wniosku. W toku postępowania mogą wystąpić formalne okoliczności dotyczące wynagrodzenia, dlatego omawiamy je spokojnie i bez skrótów.",
  },
];

export const processSteps = [
  {
    number: "01",
    title: "Bezpłatna analiza przed decyzją",
    time: "około 20 minut",
    client: "Opisujesz to, co pamiętasz. Nie musisz znać wszystkich kwot.",
    text: "Pierwsza rozmowa (ok. 20 minut) to już bezpłatna wstępna ocena Twojej sytuacji finansowej i majątkowej. Po niej wiesz, czy upadłość konsumencka jest dla Ciebie.",
  },
  {
    number: "02",
    title: "Dokumenty i wniosek",
    time: "po zebraniu potrzebnych danych",
    client: "Przesyłasz dokumenty partiami - możesz zrobić zdjęcia telefonem.",
    text: "Nie musisz mieć wszystkich dokumentów ani pamiętać wszystkich swoich zobowiązań. Przygotujemy wnioski do czterech baz danych o zadłużeniu i w razie potrzeby sprawdzimy e-sąd, żeby uzyskać pełną historię Twoich zobowiązań i niczego nie pominąć.",
  },
  {
    number: "03",
    title: "Złożenie wniosku",
    time: "po skompletowaniu",
    client: "Akceptujesz przygotowany wniosek. Resztą zajmujemy się my.",
    text: "Składamy wniosek do właściwego sądu i prowadzimy korespondencję procesową.",
  },
  {
    number: "04",
    title: "Wsparcie po ogłoszeniu upadłości",
    time: "zależnie od sprawy",
    client:
      "Zostajesz w kontakcie z naszym zespołem - bez wizyty w sądzie, jeśli nie będzie potrzebna.",
    text: "Pomagamy także przy kontakcie z syndykiem, pismach i dalszych formalnościach.",
  },
  {
    number: "05",
    title: "Plan albo umorzenie",
    time: "indywidualnie",
    client:
      "Realizujesz ustalenia, mając wsparcie przy dalszych formalnościach.",
    text: "Pomagamy w pismach do sądu i syndyka aż do zakończenia postępowania.",
  },
];

const officeBase: OfficeBase[] = [
  {
    city: "Katowice",
    slug: "katowice",
    cityIn: "Katowicach",
    address: "ul. Przemysłowa 10/20, 40-020 Katowice",
    detail: "Kancelaria główna",
    localAngle:
      "Rozmowa w centrum Katowic dla osób, które chcą zrozumieć swoją sytuację zadłużenia i uzyskać odpowiedzi.",
    nearby: "Katowice i Górny Śląsk",
    meetingNote:
      "Możesz przyjść z jednym pismem lub bez dokumentów - ustalimy, czego faktycznie potrzeba.",
    localFocus:
      "Dla mieszkańców aglomeracji śląskiej ważne są najczęściej pilne pisma, zajęcie wynagrodzenia albo wiele zobowiązań rozproszonych między wierzycielami.",
    serviceMode: "office",
    coords: { lat: 50.2534, lng: 19.0319 },
    verifiedAddress: true,
  },
  {
    city: "Żory",
    slug: "zory",
    cityIn: "Żorach",
    address: "Al. Jana Pawła II 6/1, 44-240 Żory",
    detail: "Spotkania po umówieniu",
    localAngle:
      "Spokojna rozmowa w Żorach dla osób z południowej części województwa śląskiego.",
    nearby: "Żory, Rybnik, Jastrzębie-Zdrój i okolice",
    meetingNote:
      "Na pierwszym spotkaniu możesz po prostu opowiedzieć, co się wydarzyło - bez prawniczego żargonu.",
    localFocus:
      "Jeżeli rosnące raty, komornik lub wezwania do zapłaty zabierają spokój, warto najpierw poznać fakty, a dopiero potem rozważać dalsze kroki.",
    serviceMode: "office",
    coords: { lat: 50.0453, lng: 18.7006 },
    verifiedAddress: true,
  },
  {
    city: "Łódź",
    slug: "lodz",
    cityIn: "Łodzi",
    address: "ul. Piotrkowska 148/150 lok. 4.6, 90-063 Łódź",
    detail: "Spotkania po umówieniu",
    localAngle:
      "Kancelaria przy Piotrkowskiej dla osób, które chcą omówić drogę do oddłużenia twarzą w twarz.",
    nearby: "Łódź i województwo łódzkie",
    meetingNote:
      "Po spotkaniu będziesz wiedzieć, jakie dokumenty warto przygotować i jaki może być kolejny krok.",
    localFocus:
      "W rozmowie analizujemy zobowiązania, dochody i dokumenty, aby nie zostawać samemu z listą pytań oraz nieprzeczytanymi pismami.",
    serviceMode: "office",
    coords: { lat: 51.7571, lng: 19.459 },
    verifiedAddress: true,
  },
  {
    city: "Częstochowa",
    slug: "czestochowa",
    cityIn: "Częstochowie",
    address: "ul. Dekabrystów 41/503, 42-215 Częstochowa",
    detail: "Spotkania po umówieniu",
    localAngle:
      "Lokalna rozmowa w Częstochowie, gdy komornik, pisma lub rosnące raty przestały dawać spokój.",
    nearby: "Częstochowa i północna część województwa śląskiego",
    meetingNote:
      "Możesz zabrać ostatnie pismo od komornika. Resztę omówimy wspólnie, po kolei.",
    localFocus:
      "Nie trzeba mieć gotowej teczki ani dokładnie policzonego długu - pierwszy kontakt służy ustaleniu, od czego najbezpieczniej zacząć.",
    serviceMode: "office",
    coords: { lat: 50.8076, lng: 19.1175 },
    verifiedAddress: true,
  },
  {
    city: "Bielsko-Biała",
    slug: "bielsko-biala",
    cityIn: "Bielsku-Białej",
    address: "ul. Nadbrzeżna 2, 43-300 Bielsko-Biała",
    detail: "Spotkania po umówieniu",
    localAngle:
      "Rozmowa w Bielsku-Białej dla osób, które wolą usiąść z ekspertem i wspólnie ułożyć plan działania.",
    nearby: "Bielsko-Biała, Podbeskidzie i okolice",
    meetingNote:
      "Spotkanie nie jest zobowiązaniem do zlecenia sprawy - najpierw słuchamy i wyjaśniamy.",
    localFocus:
      "Pierwszy krok może być prosty: wspólne przeczytanie jednego pisma, omówienie dochodów albo spisanie listy wierzycieli.",
    serviceMode: "office",
    coords: { lat: 49.8211, lng: 19.0441 },
    verifiedAddress: true,
  },
  {
    city: "Białystok",
    slug: "bialystok",
    cityIn: "Białymstoku",
    address: "Rozmowa online lub spotkanie po potwierdzeniu",
    detail: "Obsługa mieszkańców Białegostoku i regionu",
    localAngle:
      "Pomoc dla osób z Białegostoku, które chcą najpierw spokojnie sprawdzić swoją sytuację bez dojazdu do sądu.",
    nearby: "Białystok i województwo podlaskie",
    meetingNote:
      "Zacznij telefonicznie lub online. Jeżeli potrzebujesz spotkania na miejscu, potwierdzimy dostępność i miejsce przed rezerwacją.",
    localFocus:
      "Jeżeli długi narastały przez dłuższy czas, pierwsza rozmowa pomaga oddzielić pilne sprawy od tych, które można spokojnie przygotować.",
    serviceMode: "city",
    coords: { lat: 53.1325, lng: 23.1688 },
    verifiedAddress: false,
  },
  {
    city: "Bydgoszcz",
    slug: "bydgoszcz",
    cityIn: "Bydgoszczy",
    address: "Rozmowa online lub spotkanie po potwierdzeniu",
    detail: "Obsługa mieszkańców Bydgoszczy i regionu",
    localAngle:
      "Dyskretna pomoc w sprawach oddłużeniowych dla mieszkańców Bydgoszczy i okolic.",
    nearby: "Bydgoszcz i województwo kujawsko-pomorskie",
    meetingNote:
      "Rozmowę możesz odbyć z domu. Termin ewentualnego spotkania potwierdzamy dopiero po rozmowie z zespołem.",
    localFocus:
      "Przy wielu pożyczkach i kredytach nie musisz znać wszystkich sald na pamięć - pomagamy ułożyć informacje w logicznej kolejności.",
    serviceMode: "city",
    coords: { lat: 53.1235, lng: 18.0084 },
    verifiedAddress: false,
  },
  {
    city: "Gdańsk",
    slug: "gdansk",
    cityIn: "Gdańsku",
    address: "Rozmowa online lub spotkanie po potwierdzeniu",
    detail: "Obsługa mieszkańców Gdańska i Trójmiasta",
    localAngle:
      "Pierwsza rozmowa o upadłości konsumenckiej dla osób z Gdańska i Trójmiasta - bez oceniania i presji.",
    nearby: "Gdańsk, Trójmiasto i Pomorze",
    meetingNote:
      "Wybierasz telefon lub rozmowę online. Jeżeli zależy Ci na spotkaniu, zapytamy o dostępny wariant przed jego potwierdzeniem.",
    localFocus:
      "Gdy część dokumentów jest w banku, u komornika albo w skrzynce e-mail, zaczynamy od tego, co masz pod ręką.",
    serviceMode: "city",
    coords: { lat: 54.352, lng: 18.6466 },
    verifiedAddress: false,
  },
  {
    city: "Gorzów Wielkopolski",
    slug: "gorzow-wielkopolski",
    cityIn: "Gorzowie Wielkopolskim",
    address: "Rozmowa online lub spotkanie po potwierdzeniu",
    detail: "Obsługa mieszkańców miasta i regionu",
    localAngle:
      "Spokojne wsparcie dla mieszkańców Gorzowa Wielkopolskiego, którzy chcą zrozumieć swoje możliwości przy zadłużeniu.",
    nearby: "Gorzów Wielkopolski i województwo lubuskie",
    meetingNote:
      "Najpierw ustalamy, czy wygodniejsza będzie rozmowa telefoniczna czy online, a miejsce spotkania potwierdzamy indywidualnie.",
    localFocus:
      "Nie każda trudność finansowa wymaga tej samej drogi - rozmowa pozwala ocenić, czy warto dalej analizować upadłość konsumencką.",
    serviceMode: "city",
    coords: { lat: 52.7368, lng: 15.2288 },
    verifiedAddress: false,
  },
  {
    city: "Kielce",
    slug: "kielce",
    cityIn: "Kielcach",
    address: "Rozmowa online lub spotkanie po potwierdzeniu",
    detail: "Obsługa mieszkańców Kielc i regionu",
    localAngle:
      "Pomoc dla osób z Kielc, którym pożyczki, kredyty lub egzekucja zaczęły odbierać poczucie kontroli.",
    nearby: "Kielce i województwo świętokrzyskie",
    meetingNote:
      "Możesz wybrać rozmowę bez dojazdu. Spotkanie osobiste jest możliwe po potwierdzeniu dostępności przez zespół.",
    localFocus:
      "Na pierwszym etapie nie oczekujemy pełnego wyjaśnienia historii - wystarczy opisać to, co jest dziś najtrudniejsze.",
    serviceMode: "city",
    coords: { lat: 50.8661, lng: 20.6286 },
    verifiedAddress: false,
  },
  {
    city: "Kraków",
    slug: "krakow",
    cityIn: "Krakowie",
    address: "Rozmowa online lub spotkanie po potwierdzeniu",
    detail: "Obsługa mieszkańców Krakowa i Małopolski",
    localAngle:
      "Pomoc w zrozumieniu sytuacji dla mieszkańców Krakowa i Małopolski - od pierwszej rozmowy do dalszych formalności.",
    nearby: "Kraków i województwo małopolskie",
    meetingNote:
      "Rozmowę przeprowadzimy telefonicznie albo online. Jeżeli wolisz spotkanie, nie deklarujemy miejsca bez uprzedniego potwierdzenia.",
    localFocus:
      "Zanim podejmiesz decyzję, możesz spokojnie omówić majątek, dochód i zobowiązania bez konieczności składania deklaracji na pierwszej rozmowie.",
    serviceMode: "city",
    coords: { lat: 50.0647, lng: 19.945 },
    verifiedAddress: false,
  },
  {
    city: "Lublin",
    slug: "lublin",
    cityIn: "Lublinie",
    address: "Rozmowa online lub spotkanie po potwierdzeniu",
    detail: "Obsługa mieszkańców Lublina i regionu",
    localAngle:
      "Pomoc dla mieszkańców Lublina, którzy chcą sprawdzić, czy ich sytuacja kwalifikuje się do dalszej analizy.",
    nearby: "Lublin i województwo lubelskie",
    meetingNote:
      "Pierwsza rozmowa może odbyć się bez wychodzenia z domu. Możliwość wizyty potwierdzimy po ustaleniu potrzeb.",
    localFocus:
      "Jedno wezwanie do zapłaty lub pismo od komornika może wystarczyć, by zacząć rozmowę i wskazać kolejne dokumenty.",
    serviceMode: "city",
    coords: { lat: 51.2465, lng: 22.5684 },
    verifiedAddress: false,
  },
  {
    city: "Olsztyn",
    slug: "olsztyn",
    cityIn: "Olsztynie",
    address: "Rozmowa online lub spotkanie po potwierdzeniu",
    detail: "Obsługa mieszkańców Olsztyna i regionu",
    localAngle:
      "Dyskretna rozmowa dla mieszkańców Olsztyna, którzy potrzebują zrozumieć sytuację zadłużenia.",
    nearby: "Olsztyn i Warmia oraz Mazury",
    meetingNote:
      "Wybierz dogodny kontakt zdalny. O ewentualnym spotkaniu na miejscu porozmawiamy po potwierdzeniu dostępności.",
    localFocus:
      "W sytuacji zadłużenia ważne jest, aby nie odkładać pilnych terminów - zaczynamy od ustalenia, które pismo wymaga uwagi jako pierwsze.",
    serviceMode: "city",
    coords: { lat: 53.7784, lng: 20.4801 },
    verifiedAddress: false,
  },
  {
    city: "Opole",
    slug: "opole",
    cityIn: "Opolu",
    address: "Rozmowa online lub spotkanie po potwierdzeniu",
    detail: "Obsługa mieszkańców Opola i regionu",
    localAngle:
      "Pomoc dla osób z Opola, które chcą odzyskać orientację w swojej sytuacji finansowej.",
    nearby: "Opole i województwo opolskie",
    meetingNote:
      "Zaczynamy od telefonu albo spotkania online. Miejsce wizyty osobistej omawiamy dopiero po potwierdzeniu możliwości.",
    localFocus:
      "Przygotowanie do rozmowy nie wymaga zbierania całej dokumentacji - możemy wskazać, co warto odszukać po pierwszej konsultacji.",
    serviceMode: "city",
    coords: { lat: 50.6751, lng: 17.9213 },
    verifiedAddress: false,
  },
  {
    city: "Poznań",
    slug: "poznan",
    cityIn: "Poznaniu",
    address: "Rozmowa online lub spotkanie po potwierdzeniu",
    detail: "Obsługa mieszkańców Poznania i Wielkopolski",
    localAngle:
      "Rzeczowa pomoc dla mieszkańców Poznania, którym zadłużenie przestało pozwalać spokojnie planować codzienność.",
    nearby: "Poznań i województwo wielkopolskie",
    meetingNote:
      "Wybierz rozmowę telefoniczną lub online. Jeśli najwygodniejsze jest spotkanie, ustalimy jego dostępność przed podaniem miejsca.",
    localFocus:
      "Jeżeli masz dochód, rodzinę lub majątek, nie zakładaj z góry jednego scenariusza - te kwestie warto spokojnie omówić indywidualnie.",
    serviceMode: "city",
    coords: { lat: 52.4064, lng: 16.9252 },
    verifiedAddress: false,
  },
  {
    city: "Rzeszów",
    slug: "rzeszow",
    cityIn: "Rzeszowie",
    address: "Rozmowa online lub spotkanie po potwierdzeniu",
    detail: "Obsługa mieszkańców Rzeszowa i regionu",
    localAngle:
      "Pomoc dla mieszkańców Rzeszowa, którzy chcą zobaczyć realne możliwości zamiast dalej unikać pism i telefonów.",
    nearby: "Rzeszów i województwo podkarpackie",
    meetingNote:
      "Pierwszy kontakt odbywa się zdalnie, a ewentualne spotkanie ustalamy po rozmowie i potwierdzeniu dostępności.",
    localFocus:
      "Nawet jeśli nie znasz pełnej listy wierzycieli, można zacząć od tego, co pamiętasz i co wynika z ostatnich dokumentów.",
    serviceMode: "city",
    coords: { lat: 50.0412, lng: 21.9991 },
    verifiedAddress: false,
  },
  {
    city: "Szczecin",
    slug: "szczecin",
    cityIn: "Szczecinie",
    address: "Rozmowa online lub spotkanie po potwierdzeniu",
    detail: "Obsługa mieszkańców Szczecina i regionu",
    localAngle:
      "Pierwsza rozmowa o sytuacji zadłużenia dla mieszkańców Szczecina i województwa zachodniopomorskiego.",
    nearby: "Szczecin i województwo zachodniopomorskie",
    meetingNote:
      "Możesz rozpocząć telefonicznie lub online. O możliwości spotkania osobistego poinformujemy po weryfikacji terminu.",
    localFocus:
      "Odległość nie musi zatrzymywać rozmowy - dokumenty można kompletować etapami, a kontakt dopasować do Twojego rytmu dnia.",
    serviceMode: "city",
    coords: { lat: 53.4285, lng: 14.5528 },
    verifiedAddress: false,
  },
  {
    city: "Toruń",
    slug: "torun",
    cityIn: "Toruniu",
    address: "Rozmowa online lub spotkanie po potwierdzeniu",
    detail: "Obsługa mieszkańców Torunia i regionu",
    localAngle:
      "Pomoc dla osób z Torunia, które szukają spokojnego wyjaśnienia sytuacji zadłużenia.",
    nearby: "Toruń i województwo kujawsko-pomorskie",
    meetingNote:
      "Najpierw wskaż wygodny sposób rozmowy. Dostępność spotkania na miejscu potwierdzimy bez składania obietnic na zapas.",
    localFocus:
      "W pierwszej rozmowie możemy rozdzielić sprawy wymagające szybkiej reakcji od dokumentów, które warto przygotować spokojnie później.",
    serviceMode: "city",
    coords: { lat: 53.0138, lng: 18.5984 },
    verifiedAddress: false,
  },
  {
    city: "Warszawa",
    slug: "warszawa",
    cityIn: "Warszawie",
    address:
      "Pasaż LIM w Hotelu Presidential (dawny Marriott, naprzeciw Dworca Centralnego PKP), Al. Jerozolimskie 65/79, lokal -7, 00-698 Warszawa",
    detail: "Pasaż LIM w Hotelu Presidential, lokal -7",
    localAngle:
      "Pomoc dla mieszkańców Warszawy, którzy chcą porozmawiać o zadłużeniu konkretnie i bez marketingowych obietnic.",
    nearby: "Warszawa i Mazowsze",
    meetingNote:
      "Spotkanie w Pasażu LIM w Hotelu Presidential organizujemy po wcześniejszym potwierdzeniu telefonicznym - wybierz miasto, a ustalimy szczegóły.",
    localFocus:
      "Przy dużej liczbie zobowiązań najpierw analizujemy listę kredytów, chwilówek i egzekucji, zamiast próbować rozwiązać wszystko naraz.",
    serviceMode: "office",
    coords: { lat: 52.2297, lng: 21.0122 },
    verifiedAddress: true,
  },
  {
    city: "Wrocław",
    slug: "wroclaw",
    cityIn: "Wrocławiu",
    address: "Rozmowa online lub spotkanie po potwierdzeniu",
    detail: "Obsługa mieszkańców Wrocławia i Dolnego Śląska",
    localAngle:
      "Pomoc dla mieszkańców Wrocławia, którzy chcą ocenić drogę do oddłużenia w spokojnej rozmowie.",
    nearby: "Wrocław i Dolny Śląsk",
    meetingNote:
      "Zacznij od telefonu albo online. Jeśli potrzebujesz rozmowy stacjonarnej, potwierdzimy warunki przed jej umówieniem.",
    localFocus:
      "Nie musisz znać wszystkich przepisów ani gotowego rozwiązania - zadaniem pierwszej rozmowy jest wyjaśnienie możliwości.",
    serviceMode: "city",
    coords: { lat: 51.1079, lng: 17.0385 },
    verifiedAddress: false,
  },
  {
    city: "Zielona Góra",
    slug: "zielona-gora",
    cityIn: "Zielonej Górze",
    address: "Rozmowa online lub spotkanie po potwierdzeniu",
    detail: "Obsługa mieszkańców Zielonej Góry i regionu",
    localAngle:
      "Dyskretna pomoc dla mieszkańców Zielonej Góry, którzy chcą zacząć od spokojnego wyjaśnienia sytuacji.",
    nearby: "Zielona Góra i województwo lubuskie",
    meetingNote:
      "Najpierw wybierasz wygodną rozmowę. Ewentualne spotkanie osobiste wymaga potwierdzenia miejsca i terminu przez zespół.",
    localFocus:
      "Jeżeli najbardziej stresuje Cię jedno pismo albo termin, zacznij od niego - pomożemy ustalić, co wynika z dokumentu i co można zrobić dalej.",
    serviceMode: "city",
    coords: { lat: 51.9356, lng: 15.5062 },
    verifiedAddress: false,
  },
];

const cityDetails: Record<string, CityDetails> = {
  katowice: {
    name: "Katowice",
    gen: "Katowic",
    loc: "w Katowicach",
    type: "office",
    address: "ul. Przemysłowa 10/20, 40-020 Katowice",
    googleListingUrl: "https://maps.app.goo.gl/vvN72M9yVmED9DTB7",
    googleListingQuery:
      "Upadłość Konsumencka Katowice | Kancelaria Oddłużeniowa Upadłości-Ekspert",
    heroMedia: {
      src: "/manus-storage/katowice-kancelaria-wnetrze_1b80330a.webp",
      width: 1080,
      height: 1440,
      alt: "Wnętrze kancelarii Upadłości Ekspert w Katowicach",
    },
    galleryMedia: [
      {
        src: "/manus-storage/katowice-biurowiec_13fe72e8.webp",
        width: 1440,
        height: 810,
        alt: "Biurowiec kancelarii Upadłości Ekspert w Katowicach",
        caption: "Biurowiec kancelarii w Katowicach.",
      },
      {
        src: "/manus-storage/katowice-biurowiec-2_07c3ebef.webp",
        width: 1440,
        height: 812,
        alt: "Biurowiec kancelarii Upadłości Ekspert w Katowicach",
        caption: "Widok budynku kancelarii w Katowicach.",
      },
    ],
    nearby: [
      "Chorzów",
      "Sosnowiec",
      "Mysłowice",
      "Siemianowice Śląskie",
      "Ruda Śląska",
      "Tychy",
      "Mikołów",
    ],
    courtInfo: "",
  },
  zory: {
    name: "Żory",
    gen: "Żor",
    loc: "w Żorach",
    type: "office",
    address: "Al. Jana Pawła II 6/1, 44-240 Żory",
    googleListingUrl: "https://maps.app.goo.gl/869sEGMKzPKXX5dGA",
    googleListingQuery:
      "Upadłość Konsumencka Żory | Kancelaria Oddłużeniowa Upadłości-Ekspert",
    officePhoto: "/manus-storage/biuro-zory-zewnatrz_08d90edb.webp",
    locationMedia: [
      {
        src: "/manus-storage/biuro-zory-zewnatrz_08d90edb.webp",
        width: 900,
        height: 620,
        alt: "Wejście do kancelarii Upadłości Ekspert w Żorach",
        caption: "Wejście do kancelarii Upadłości Ekspert w Żorach.",
      },
    ],
    heroMedia: {
      src: "/manus-storage/zory-jowita-hero_b5764def.webp",
      width: 1536,
      height: 2048,
      alt: "Pracowniczka kancelarii Upadłości Ekspert w Żorach",
    },
    nearby: [
      "Rybnik",
      "Jastrzębie-Zdrój",
      "Wodzisław Śląski",
      "Pszczyna",
      "Orzesze",
      "Suszec",
    ],
    courtInfo: "",
  },
  lodz: {
    name: "Łódź",
    gen: "Łodzi",
    loc: "w Łodzi",
    type: "office",
    address: "ul. Piotrkowska 148/150 lok. 4.6, 90-063 Łódź",
    googleListingUrl: "https://share.google/sb1T3SyBPGbIoFDUV",
    googleListingQuery:
      "Upadłość konsumencka Łódź | Kancelaria Oddłużeniowa Upadłości-Ekspert",
    googleListingCid: "341754777426873697",
    heroMedia: {
      src: "/manus-storage/lodz-kancelaria-karolina_812a138f.webp",
      width: 1440,
      height: 810,
      alt: "Kancelaria Upadłości Ekspert w Łodzi",
    },
    locationMedia: [
      {
        src: "/manus-storage/lodz-red-tower-przed-wejsciem_545f2ce0.webp",
        width: 595,
        height: 652,
        alt: "Przed wejściem do kancelarii Upadłości Ekspert w Red Tower w Łodzi",
        caption: "Przed wejściem do kancelarii w Red Tower w Łodzi.",
      },
    ],
    nearby: [
      "Pabianice",
      "Zgierz",
      "Aleksandrów Łódzki",
      "Konstantynów Łódzki",
      "Rzgów",
      "Brzeziny",
    ],
    courtInfo: "",
  },
  czestochowa: {
    name: "Częstochowa",
    gen: "Częstochowy",
    loc: "w Częstochowie",
    type: "office",
    address: "ul. Dekabrystów 41/503, 42-215 Częstochowa",
    googleListingUrl: "https://share.google/i6bbnQf41No8whHdv",
    googleListingQuery:
      "Upadłość konsumencka Częstochowa | Kancelaria Oddłużeniowa Upadłości-Ekspert",
    googleListingCid: "14656968588509402353",
    heroMedia: sharedCityHeroMedia,
    locationMedia: [
      {
        src: "/manus-storage/czestochowa-biurowiec_a538987e.webp",
        width: 795,
        height: 921,
        alt: "Biurowiec kancelarii Upadłości Ekspert w Częstochowie",
        caption: "Biurowiec kancelarii w Częstochowie.",
      },
      {
        src: "/manus-storage/czestochowa-kancelaria-wnetrze_f046448b.webp",
        width: 1440,
        height: 812,
        alt: "Wnętrze kancelarii Upadłości Ekspert w Częstochowie",
        caption: "Wnętrze kancelarii w Częstochowie.",
      },
    ],
    nearby: ["Myszków", "Kłobuck", "Blachownia", "Mykanów", "Poczesna"],
    courtInfo: "",
  },
  "bielsko-biala": {
    name: "Bielsko-Biała",
    gen: "Bielska-Białej",
    loc: "w Bielsku-Białej",
    type: "office",
    address: "ul. Nadbrzeżna 2, 43-300 Bielsko-Biała",
    googleListingUrl: "https://maps.app.goo.gl/JsTm8a1FyLXjpLu39",
    googleListingQuery:
      "Upadłość Konsumencka Bielsko-Biała | Kancelaria Oddłużeniowa Upadłości-Ekspert",
    heroMedia: sharedCityHeroMedia,
    nearby: [
      "Czechowice-Dziedzice",
      "Żywiec",
      "Kęty",
      "Cieszyn",
      "Skoczów",
      "Szczyrk",
    ],
    courtInfo: "",
  },
  bialystok: {
    name: "Białystok",
    gen: "Białegostoku",
    loc: "w Białymstoku",
    type: "remote",
    heroMedia: sharedCityHeroMedia,
    nearby: ["Wasilków", "Choroszcz", "Łapy", "Supraśl", "Zabłudów"],
    courtInfo: "",
  },
  bydgoszcz: {
    name: "Bydgoszcz",
    gen: "Bydgoszczy",
    loc: "w Bydgoszczy",
    type: "remote",
    heroMedia: sharedCityHeroMedia,
    nearby: [
      "Solec Kujawski",
      "Nakło nad Notecią",
      "Koronowo",
      "Białe Błota",
      "Osielsko",
    ],
    courtInfo: "",
  },
  gdansk: {
    name: "Gdańsk",
    gen: "Gdańska",
    loc: "w Gdańsku",
    type: "remote",
    heroMedia: sharedCityHeroMedia,
    nearby: ["Gdynia", "Sopot", "Pruszcz Gdański", "Rumia", "Tczew"],
    courtInfo: "",
  },
  "gorzow-wielkopolski": {
    name: "Gorzów Wielkopolski",
    gen: "Gorzowa Wielkopolskiego",
    loc: "w Gorzowie Wielkopolskim",
    type: "remote",
    heroMedia: sharedCityHeroMedia,
    nearby: ["Kostrzyn nad Odrą", "Witnica", "Deszczno", "Santok", "Kłodawa"],
    courtInfo: "",
  },
  kielce: {
    name: "Kielce",
    gen: "Kielc",
    loc: "w Kielcach",
    type: "remote",
    heroMedia: sharedCityHeroMedia,
    nearby: ["Chęciny", "Morawica", "Masłów", "Daleszyce", "Piekoszów"],
    courtInfo: "",
  },
  krakow: {
    name: "Kraków",
    gen: "Krakowa",
    loc: "w Krakowie",
    type: "remote",
    heroMedia: sharedCityHeroMedia,
    nearby: ["Wieliczka", "Skawina", "Niepołomice", "Zabierzów", "Zielonki"],
    courtInfo: "",
  },
  lublin: {
    name: "Lublin",
    gen: "Lublina",
    loc: "w Lublinie",
    type: "remote",
    heroMedia: sharedCityHeroMedia,
    nearby: ["Świdnik", "Lubartów", "Łęczna", "Bełżyce", "Niemce"],
    courtInfo: "",
  },
  olsztyn: {
    name: "Olsztyn",
    gen: "Olsztyna",
    loc: "w Olsztynie",
    type: "remote",
    heroMedia: sharedCityHeroMedia,
    nearby: ["Dywity", "Barczewo", "Stawiguda", "Olsztynek", "Dobre Miasto"],
    courtInfo: "",
  },
  opole: {
    name: "Opole",
    gen: "Opola",
    loc: "w Opolu",
    type: "remote",
    heroMedia: sharedCityHeroMedia,
    nearby: ["Krapkowice", "Ozimek", "Prószków", "Dobrzeń Wielki", "Niemodlin"],
    courtInfo: "",
  },
  poznan: {
    name: "Poznań",
    gen: "Poznania",
    loc: "w Poznaniu",
    type: "remote",
    heroMedia: sharedCityHeroMedia,
    nearby: ["Swarzędz", "Luboń", "Komorniki", "Suchy Las", "Mosina"],
    courtInfo: "",
  },
  rzeszow: {
    name: "Rzeszów",
    gen: "Rzeszowa",
    loc: "w Rzeszowie",
    type: "remote",
    heroMedia: sharedCityHeroMedia,
    nearby: ["Łańcut", "Strzyżów", "Boguchwała", "Tyczyn", "Głogów Małopolski"],
    courtInfo: "",
  },
  szczecin: {
    name: "Szczecin",
    gen: "Szczecina",
    loc: "w Szczecinie",
    type: "remote",
    heroMedia: sharedCityHeroMedia,
    nearby: ["Police", "Stargard", "Gryfino", "Goleniów", "Dobra"],
    courtInfo: "",
  },
  torun: {
    name: "Toruń",
    gen: "Torunia",
    loc: "w Toruniu",
    type: "remote",
    heroMedia: sharedCityHeroMedia,
    nearby: [
      "Chełmża",
      "Lubicz",
      "Zławieś Wielka",
      "Obrowo",
      "Aleksandrów Kujawski",
    ],
    courtInfo: "",
  },
  warszawa: {
    name: "Warszawa",
    gen: "Warszawy",
    loc: "w Warszawie",
    type: "office",
    googleListingUrl: "https://share.google/D7XYghNPEdMZfi0hK",
    googleListingQuery:
      "Upadłość konsumencka Warszawa | Kancelaria Oddłużeniowa Upadłości-Ekspert",
    googleListingCid: "6685948162299900901",
    heroMedia: {
      src: "/manus-storage/warszawa-kancelaria-piotr_2b6cb76b.webp",
      width: 1440,
      height: 1080,
      alt: "Kancelaria Upadłości Ekspert w Warszawie",
    },
    locationMedia: [
      {
        src: "/manus-storage/presidential-hotel-warsaw-cc-by-4_689a52d7.webp",
        width: 1080,
        height: 1357,
        alt: "Hotel Presidential w Warszawie przy Al. Jerozolimskich",
        caption:
          "Hotel Presidential, w którego Pasażu LIM znajduje się kancelaria.",
        attribution: {
          author: "Igor123121",
          sourceUrl:
            "https://commons.wikimedia.org/wiki/File:Presidential_Hotel,_2026,_Warsaw,_Poland.jpg",
          license: "CC BY 4.0",
          licenseUrl: "https://creativecommons.org/licenses/by/4.0/deed.pl",
          changeNote: "Zdjęcie przeskalowano i zapisano w formacie WebP.",
        },
      },
    ],
    nearby: ["Piaseczno", "Pruszków", "Legionowo", "Marki", "Ząbki", "Otwock"],
    courtInfo: "",
  },
  wroclaw: {
    name: "Wrocław",
    gen: "Wrocławia",
    loc: "we Wrocławiu",
    type: "remote",
    heroMedia: sharedCityHeroMedia,
    nearby: [
      "Oleśnica",
      "Oława",
      "Siechnice",
      "Kąty Wrocławskie",
      "Długołęka",
      "Środa Śląska",
    ],
    courtInfo: "",
  },
  "zielona-gora": {
    name: "Zielona Góra",
    gen: "Zielonej Góry",
    loc: "w Zielonej Górze",
    type: "remote",
    heroMedia: sharedCityHeroMedia,
    nearby: ["Nowa Sól", "Sulechów", "Świebodzin", "Czerwieńsk", "Zabór"],
    courtInfo: "",
  },
};

export const offices: Office[] = officeBase.map(office => {
  const details = cityDetails[office.slug];
  const mapsUrl =
    details.type === "office" && office.verifiedAddress && office.address
      ? details.googleListingUrl
      : undefined;
  return {
    ...office,
    ...details,
    city: details.name,
    regionLine: office.localAngle,
    mapsUrl,
  };
});

export const physicalOffices = offices.filter(
  office => office.type === "office"
);
export const mappedOffices = physicalOffices.filter(
  office => office.verifiedAddress
);
export const servicedCities = offices.filter(
  office => office.type === "remote"
);

/**
 * Google potrafi inaczej zinterpretować samą nazwę firmy niż przekazaną
 * wizytówkę. Dla wskazanych przez klienta lokalizacji stosujemy więc trwały
 * identyfikator wizytówki, a nie wynik wyszukiwania po nazwie.
 */
export function getOfficeMapEmbedUrl(
  office: Pick<
    Office,
    "googleListingCid" | "googleListingQuery" | "address" | "city"
  >
) {
  if (office.googleListingCid) {
    return `https://maps.google.com/maps?cid=${office.googleListingCid}&output=embed`;
  }

  const mapQuery = encodeURIComponent(
    office.googleListingQuery ?? `${office.address}, ${office.city}`
  );
  return `https://maps.google.com/maps?q=${mapQuery}&t=m&z=15&output=embed&iwloc=near`;
}

export function getLocalFaqItems(office: Office): FaqItem[] {
  const localFirst =
    office.type === "office"
      ? {
          question: `Czy muszę przyjechać do ${office.gen}?`,
          answer: `Nie. Możesz rozpocząć telefonicznie lub online, ale jeśli preferujesz spotkanie, zapraszamy do kancelarii ${office.loc} po wcześniejszym potwierdzeniu terminu.`,
        }
      : {
          question: "Czy muszę jechać do kancelarii?",
          answer:
            "Nie. Całą sprawę można prowadzić telefonicznie i online, a dokumenty wysłać zdjęciem z telefonu. Spotkanie jest możliwe po wcześniejszym potwierdzeniu dostępności.",
        };
  const findAnswer = (question: string) =>
    faqItems.find(item => item.question === question)!;
  return [
    localFirst,
    {
      question: "Czy muszę przygotować wszystkie dokumenty?",
      answer:
        "Nie. Wystarczy jedno ostatnie pismo, przybliżona informacja o długach lub opis tego, co jest dziś najpilniejsze. Dokumenty kompletujemy etapami.",
    },
    {
      question: "Czy spotkanie lub rozmowa mnie do czegoś zobowiązuje?",
      answer:
        "Nie. Pierwszy kontakt służy ocenie sytuacji i wyjaśnieniu możliwości. Decyzję o dalszych krokach podejmujesz dopiero po otrzymaniu jasnych informacji.",
    },
    findAnswer("Czy stracę mieszkanie?"),
    findAnswer("Czy dowie się mój pracodawca?"),
    findAnswer("Czy muszę iść do sądu?"),
  ];
}

export const googleReviews = [
  {
    name: "Artur Śniegoń",
    when: "7 miesięcy temu",
    text: "Dzięki profesjonalnej pomocy całego zespołu, proces upadłości konsumenckiej przebiegł sprawnie i bez niepotrzebnego stresu. Wszystko było jasno wytłumaczone, a kontakt na każdym etapie bardzo dobry. Z czystym sumieniem polecam.",
  },
  {
    name: "Maria Marysia",
    when: "7 miesięcy temu",
    text: "Nie wierzyłam, że to się uda, ale naprawdę pozbyłam się długów z pomocą tych ludzi. Słowo dziękuję to za mało. Polecam każdemu, kto ma problemy z komornikiem, windykacja. Bardzo miła obsługa, widać że to doświadczeni ludzie i wiedzą co robią. Nie ma rzeczy niemożliwych",
  },
  {
    name: "Przemek Łętowski",
    when: "rok temu",
    text: "Bardzo polecam❗️kompetentni ludzie! Pomagają odpowiedzieć na każde pismo które się otrzyma jak i je wyjaśnić jeśli pojawi się jakieś pytanie❗️są otwarci na współpracę zdalną jest to kolejny plus ponieważ nie trzeba udawać się do kancelarii. Na wszelkie zapytania otrzymuje się odpowiedź tego samego dnia. Polecam gorąco jeszcze raz❗️",
  },
  {
    name: "Krzysztof Załupka",
    when: "rok temu",
    text: "Bardzo dziękuję za pomoc w upadłości konsumenckiej. Dzięki Waszej pomocy moje życie wróciło do normalności. Wreszcie mogę żyć spokojnie na emeryturze!",
  },
  {
    name: "Andrzej Bińkowski",
    when: "rok temu",
    text: "Bardzo dziękuję za wszelką pomoc całemu zespołowi za powrót do normalności. Wreszcie mogę się cieszyć całą wypłatą bez jakichkolwiek zmartwień. Polecam każdemu kto ma problemy z długami. Jeszcze raz wielki dzięki dla Pani Jowity, Pana Patryka i Pana Piotra!",
  },
  {
    name: "Cristian Wild",
    when: "2 lata temu",
    text: "Polecam to za mało powiedziane. Aktualnie jestem dosłownie kilka dni po ogłoszonej przez sąd upadłości. Wniosek sporządzony fachowo i szybko dodatkowo doradztwo na każdym kroku i błyskawiczne odpowiedzi na maile z moimi różnymi pytaniami czy wątpliwościami. W razie potrzeby można umówić się na spotkanie i porozmawiać na konkretne tematy. Z mojego punktu widzenia firma wykazała się pełnym profesjonalizmem. Pan Piotr prawdziwy specjalista zawsze chętny doradzić. POLECAM KAŻDEMU !!!",
  },
];

export const opinionTabs = [
  {
    id: "komornik",
    label: "miałem komornika",
    reviews: [
      {
        name: "Andrzej B.",
        text: "Wreszcie mogę się cieszyć całą wypłatą bez zmartwień. Dziękuję za pomoc całemu zespołowi.",
      },
      {
        name: "Maria M.",
        text: "Nie wierzyłam, że się uda, ale dostałam pomoc w trudnej sytuacji z komornikiem i windykacją.",
      },
      {
        name: "Przemek Ł.",
        text: "Dużym plusem była współpraca zdalna i odpowiedzi na pytania na każdym etapie.",
      },
    ],
  },
  { id: "firma", label: "byłem przedsiębiorcą", reviews: [] },
  {
    id: "emerytura",
    label: "jestem na emeryturze",
    reviews: [
      {
        name: "Krzysztof Z.",
        text: "Dzięki pomocy moje życie wróciło do normalności. Wreszcie mogę żyć spokojnie na emeryturze.",
      },
    ],
  },
  { id: "chwilowki", label: "miałem chwilówki", reviews: [] },
  { id: "kancelaria", label: "zostałem sam po innej kancelarii", reviews: [] },
];

export const nonFitItems = [
  {
    title: "Masz przejściową trudność, ale możesz regulować raty",
    text: "Gdy problem ma charakter krótkotrwały, zwykle warto najpierw sprawdzić ugodę, wakacje kredytowe albo restrukturyzację zobowiązania.",
  },
  {
    title: "Prowadzisz aktywną działalność gospodarczą",
    text: "Sytuacja przedsiębiorcy wymaga odrębnej analizy. Najpierw trzeba ustalić, czy właściwą drogą nie będzie postępowanie dotyczące działalności.",
  },
  {
    title: "Masz wyłącznie jeden niewielki dług",
    text: "Przy zadłużeniu niższym niż 30 tys. zł koszty i czas postępowania mogą nie być proporcjonalne. Podpowiemy, jakie inne kroki rozważyć.",
  },
  {
    title: "Potrzebujesz wyłącznie szybkiej pożyczki",
    text: "Nie pomożemy w zaciąganiu kolejnego zobowiązania. Rozmowa ma dać Ci konkretną odpowiedź, a nie odkładać decyzję.",
  },
  {
    title: "Ukrywasz majątek albo nie chcesz ujawnić zobowiązań",
    text: "Postępowanie wymaga pełnej i rzetelnej informacji. Zatajanie faktów może zaszkodzić sprawie; uczciwie wyjaśnimy ryzyko.",
  },
];
